// React and core hooks
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

// External libraries
import { neon, type User } from "@/integrations/neon";

// Hooks & utils
import { useToast } from "@/hooks/use-toast";
import { useContacts } from "@/hooks/useContacts";
import { copyTextToClipboard } from "@/utils/share";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n/useTranslation";
import { getErrorMessage, logError } from "@/utils/errors";

// Feature modules
import { DashboardHeader } from "@/features/dashboard/components";
import { useSortedScripts } from "@/features/dashboard/utils";
import type { Script, ScriptForm, AvailableForm } from "@/features/dashboard/types";
import { DashboardList } from "@/features/dashboard/components/DashboardList";
import { ParticipantsView } from "@/features/dashboard/components/ParticipantsView";
import { useDashboardData } from "@/features/dashboard/hooks/useDashboardData";
import { DashboardDialogsProvider } from "@/features/dashboard/context";
import { useDashboardDialogOrchestration } from "@/features/dashboard/hooks/useDashboardDialogOrchestration";

// Local components
import { AddScriptDialog } from "./AddScriptDialog";
import { SettingsDialog } from "./SettingsDialog";
import { ScriptAnalysisDialog } from "./ScriptAnalysisDialog";
import { ManageParticipantsDialog } from "@/components/participants/ManageParticipantsDialog";
import { ShareScriptFormDialog } from "@/components/participants/ShareScriptFormDialog";

interface DashboardProps {
  user: User;
  onSignOut: () => void;
}

export const Dashboard = (props: DashboardProps) => {
  return (
    <DashboardDialogsProvider>
      <DashboardContent {...props} />
    </DashboardDialogsProvider>
  );
};

const LoadingState = ({ message }: { message: string }) => (
  <div className="flex min-h-[40vh] items-center justify-center">
    <div className="text-center">
      <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
      <p className="text-muted-foreground">{message}</p>
    </div>
  </div>
);

interface MainSectionProps {
  isInitialLoading: boolean;
  loadingMessage: string;
  prefersReducedMotion: boolean;
  viewAnimationStyle: CSSProperties;
  showParticipantsView: boolean;
  scripts: Script[];
  sortedScripts: Script[];
  availableFormsByScript: Record<string, AvailableForm[]>;
  addingForm: { scriptId: string; formId: string } | null;
  formatDate: (date: string) => string;
  onCopy: (text: string, label?: string) => Promise<void>;
  onAddForm: (script: Script, formId: string) => Promise<void>;
  onShare: (script: Script, scriptForm: ScriptForm) => void;
  onManageParticipants: (script: Script, scriptForm: ScriptForm) => void;
  onDeleteScript: (scriptId: string) => Promise<void>;
  onAnalyze: (scriptForm: ScriptForm, scriptName: string, script: Script) => void;
  onRefresh: () => Promise<void>;
}

const renderMainSection = ({
  isInitialLoading,
  loadingMessage,
  prefersReducedMotion,
  viewAnimationStyle,
  showParticipantsView,
  scripts,
  sortedScripts,
  availableFormsByScript,
  addingForm,
  formatDate,
  onCopy,
  onAddForm,
  onShare,
  onManageParticipants,
  onDeleteScript,
  onAnalyze,
  onRefresh,
}: MainSectionProps) => (
  <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
    {isInitialLoading ? (
      <LoadingState message={loadingMessage} />
    ) : (
      <div
        className={cn("relative", !prefersReducedMotion && "motion-safe:transition-none")}
        style={viewAnimationStyle}
      >
        {showParticipantsView ? (
          <ParticipantsView
            scripts={scripts}
            availableFormsByScript={availableFormsByScript}
            addingForm={addingForm}
            formatDate={formatDate}
            onCopy={onCopy}
            onAddForm={onAddForm}
            onShare={onShare}
            onManageParticipants={onManageParticipants}
            onDeleteScript={onDeleteScript}
          />
        ) : (
          <DashboardList
            scripts={sortedScripts}
            formatDate={formatDate}
            onShare={onShare}
            onAnalyze={onAnalyze}
            onAddForm={onAddForm}
            availableFormsByScript={availableFormsByScript}
            addingForm={addingForm}
            onRefresh={onRefresh}
          />
        )}
      </div>
    )}
  </main>
);

type DialogHandlers = ReturnType<typeof useDashboardDialogOrchestration>;
type ContactList = ReturnType<typeof useContacts>["contacts"];
type RefreshContacts = ReturnType<typeof useContacts>["refresh"];

interface DashboardDialogsProps {
  dialogs: DialogHandlers;
  contacts: ContactList;
  refreshContacts: RefreshContacts;
  resolveFormName: (scriptForm?: ScriptForm | null) => string;
  refetchScripts: () => Promise<void>;
}

const DashboardDialogs = ({ dialogs, contacts, refreshContacts, resolveFormName, refetchScripts }: DashboardDialogsProps) => {
  const {
    showAddScript,
    openAddScript,
    closeAddScript,
    showSettings,
    openSettings,
    closeSettings,
    manageParticipantsState,
    openManageParticipants,
    closeManageParticipants,
    shareDialogState,
    openShareDialog,
    closeShareDialog,
    selectedScriptForm,
    handleAnalysisDialogClose,
  } = dialogs;

  const handleAddScriptChange = (open: boolean) => {
    if (open) {
      openAddScript();
    } else {
      closeAddScript();
    }
  };

  const handleSettingsChange = (open: boolean) => {
    if (open) {
      openSettings();
    } else {
      closeSettings();
    }
  };

  const handleParticipantsChange = (open: boolean) => {
    if (!open) {
      closeManageParticipants();
    } else if (manageParticipantsState.scriptForm) {
      openManageParticipants({
        scriptForm: manageParticipantsState.scriptForm,
        scriptName: manageParticipantsState.scriptName,
      });
    }
  };

  const handleShareChange = (open: boolean) => {
    if (!open) {
      closeShareDialog();
    } else if (shareDialogState.script && shareDialogState.scriptForm) {
      openShareDialog(shareDialogState.script, shareDialogState.scriptForm);
    }
  };

  return (
    <>
      <AddScriptDialog
        open={showAddScript}
        onOpenChange={handleAddScriptChange}
        onScriptAdded={() => {
          void refetchScripts();
        }}
      />

      <SettingsDialog open={showSettings} onOpenChange={handleSettingsChange} />

      <ManageParticipantsDialog
        open={manageParticipantsState.open && !!manageParticipantsState.scriptForm}
        onOpenChange={handleParticipantsChange}
        scriptFormId={manageParticipantsState.scriptForm?.id ?? null}
        formName={resolveFormName(manageParticipantsState.scriptForm)}
        participants={manageParticipantsState.scriptForm?.script_form_participants ?? []}
        contacts={contacts}
        refreshContacts={refreshContacts}
        onParticipantsUpdated={() => {
          void refetchScripts();
        }}
      />

      <ShareScriptFormDialog
        open={shareDialogState.open && !!shareDialogState.script && !!shareDialogState.scriptForm}
        onOpenChange={handleShareChange}
        scriptFormId={shareDialogState.scriptForm?.id ?? null}
        formName={resolveFormName(shareDialogState.scriptForm ?? null)}
        movieName={shareDialogState.script?.movie_name ?? ""}
        reviewLink={shareDialogState.scriptForm?.review_link ?? null}
        currentDueDate={shareDialogState.scriptForm?.due_date ?? null}
        password={shareDialogState.script?.password_protected ? shareDialogState.script.review_password : null}
        participants={shareDialogState.scriptForm?.script_form_participants ?? []}
        contacts={contacts}
        refreshContacts={refreshContacts}
        onParticipantsUpdated={() => {
          void refetchScripts();
        }}
      />

      {selectedScriptForm && (
        <ScriptAnalysisDialog
          scriptForm={selectedScriptForm.scriptForm}
          scriptName={selectedScriptForm.scriptName}
          script={selectedScriptForm.script}
          open={!!selectedScriptForm}
          onOpenChange={(open) => {
            if (!open) {
              handleAnalysisDialogClose();
            }
          }}
        />
      )}
    </>
  );
};

const DashboardContent = ({ user, onSignOut }: DashboardProps) => {
  const { toast } = useToast();
  const { contacts, refresh: refreshContacts } = useContacts();
  const {
    scripts,
    isLoading,
    isFetching,
    availableFormsByScript,
    refetchScripts,
    loadUserForms,
    addFormToScript,
    deleteScript,
  } = useDashboardData({ userId: user.id, toast });
  const { t, language } = useTranslation();

  const dialogHandlers = useDashboardDialogOrchestration(scripts);

  const {
    showAddScript,
    openAddScript,
    closeAddScript,
    showSettings,
    openSettings,
    closeSettings,
    shareDialogState,
    openShareDialog,
    closeShareDialog,
    manageParticipantsState,
    openManageParticipants,
    closeManageParticipants,
    selectedScriptForm,
    handleShareScript,
    handleManageParticipants,
    handleAnalyze,
    handleAnalysisDialogClose,
  } = dialogHandlers;

  const [showParticipantsView, setShowParticipantsView] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [sortOption, setSortOption] = useState<"created_desc" | "created_asc" | "average_desc" | "completion_desc">("created_desc");
  const [addingForm, setAddingForm] = useState<{ scriptId: string; formId: string } | null>(null);

  const sortedScripts = useSortedScripts(scripts, sortOption);
  const isInitialLoading = isLoading || (isFetching && scripts.length === 0);

  const sortLabel = useMemo(() => {
    switch (sortOption) {
      case "created_asc":
        return t("dashboard.sort_label.created_asc");
      case "average_desc":
        return t("dashboard.sort_label.average_desc");
      case "completion_desc":
        return t("dashboard.sort_label.completion_desc");
      default:
        return t("dashboard.sort_label.created_desc");
    }
  }, [sortOption, t]);

  const viewAnimationStyle: CSSProperties = useMemo(() => {
    if (prefersReducedMotion) {
      return {};
    }
    return {
      opacity: showParticipantsView ? 0.95 : 1,
      transition: "opacity 300ms ease",
    };
  }, [prefersReducedMotion, showParticipantsView]);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const resolveCopyLabel = useCallback((label: string) => {
    switch (label.toLowerCase()) {
      case "review link":
        return t("dashboard.copy_labels.review_link");
      case "password":
        return t("dashboard.copy_labels.password");
      default:
        return t("dashboard.copy_labels.link");
    }
  }, [t]);

  const copyToClipboard = useCallback(async (text: string, label: string = "Link") => {
    const translatedLabel = resolveCopyLabel(label);
    try {
      await copyTextToClipboard(text);
      toast({
        title: t("dashboard.copy_toast.success_title", { label: translatedLabel }),
        description: t("dashboard.copy_toast.success_description"),
      });
    } catch (error) {
      console.error("Failed to copy text", error);
      toast({
        title: t("dashboard.copy_toast.failure_title"),
        description: t("dashboard.copy_toast.failure_description"),
        variant: "destructive",
      });
    }
  }, [toast, t, resolveCopyLabel]);

  const resolveFormName = useCallback((scriptForm?: ScriptForm | null) => {
    if (!scriptForm) {
      return "";
    }
    const form = scriptForm.feedback_forms;
    if (language === "th" && form.name_th) {
      return form.name_th;
    }
    return form.name;
  }, [language]);

  const handleAddForm = useCallback(async (script: Script, formId: string) => {
    setAddingForm({ scriptId: script.id, formId });
    try {
      await addFormToScript(script, formId);
    } finally {
      setAddingForm(null);
    }
  }, [addFormToScript]);

  const handleDeleteScript = useCallback(async (scriptId: string) => {
    await deleteScript(scriptId);
  }, [deleteScript]);

  const refreshScripts = useCallback(async () => {
    await refetchScripts();
  }, [refetchScripts]);

  const handleSignOut = useCallback(async () => {
    try {
      await neon.auth.signOut();
      onSignOut();
    } catch (error) {
      logError(error, { feature: "auth", action: "signOut", userId: user.id });
      toast({
        title: t("dashboard.signout_error_title"),
        description: getErrorMessage(error, t("dashboard.signout_error_description")),
        variant: "destructive",
      });
    }
  }, [onSignOut, toast, t, user.id]);

  const previousShowSettings = useRef(showSettings);
  useEffect(() => {
    if (previousShowSettings.current && !showSettings) {
      loadUserForms(true);
    }
    previousShowSettings.current = showSettings;
  }, [showSettings, loadUserForms]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const loadingMessage = t("dashboard.loading");

  const mainSection = renderMainSection({
    isInitialLoading,
    loadingMessage,
    prefersReducedMotion,
    viewAnimationStyle,
    showParticipantsView,
    scripts,
    sortedScripts,
    availableFormsByScript,
    addingForm,
    formatDate,
    onCopy: copyToClipboard,
    onAddForm: handleAddForm,
    onShare: handleShareScript,
    onManageParticipants: handleManageParticipants,
    onDeleteScript: handleDeleteScript,
    onAnalyze: handleAnalyze,
    onRefresh: refreshScripts,
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100/70 to-blue-50/50 pb-16">
      <DashboardHeader
        user={user}
        showParticipantsView={showParticipantsView}
        sortOption={sortOption}
        sortLabel={sortLabel}
        onToggleParticipantsView={() => setShowParticipantsView((previous) => !previous)}
        onSortChange={setSortOption}
        onAddScript={openAddScript}
        onSettings={openSettings}
        onSignOut={handleSignOut}
      />

      {mainSection}

      <DashboardDialogs
        dialogs={dialogHandlers}
        contacts={contacts}
        refreshContacts={refreshContacts}
        resolveFormName={resolveFormName}
        refetchScripts={refreshScripts}
      />
    </div>
  );
};
